import { Fragment, useEffect, useRef, useState } from "react";
import type { FC, Ref } from "react";
import { Button } from "@/components/ui/button";
import { MoreHorizontal, MoreVertical } from "lucide-react";
import { ButtonVariant } from "@/constants/button/button";
import { cn } from "@/lib/utils";

interface ActionMenuItem {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  hidden?: boolean;
  divider?: boolean; // render separator above
  icon?: React.ReactNode; // optional icon
}

interface ActionMenuProps {
  items: ActionMenuItem[]
  menuType: "vertical" | "horizontal"
  triggerAriaLabel?: string
  triggerRef?: Ref<HTMLButtonElement>
}

export const ActionMenu: FC<ActionMenuProps> = ({ items, menuType, triggerAriaLabel, triggerRef }) => {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="inline-block items-center justify-center" ref={menuRef}>
      <Button
        ref={triggerRef}
        variant={ButtonVariant.GHOST}
        className="h-8 w-8 p-0 hover:text-secondary hover:bg-transparent"
        type="button"
        aria-label={triggerAriaLabel}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((prev) => !prev);
        }}
      >
        {menuType === "vertical" ? <MoreVertical /> : <MoreHorizontal />}
      </Button>
      {open && (
        <div className="absolute right-8 p-1 mt-1 w-44 bg-white border rounded-md shadow-lg z-50">
          {items.filter(item => !item.hidden).map((item, idx) => (
            <Fragment key={idx}>
              {item.divider && <hr className="my-1 border-gray-200" />}
              <button
                disabled={item.disabled}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!item.disabled) {
                    item.onClick();
                    setOpen(false);
                  }
                }}
                className={cn(
                  "w-full flex text-primary items-center justify-start text-left px-3 py-2 text-sm gap-2 hover:bg-gray-100",
                  item.disabled && "text-gray-300 cursor-not-allowed"
                )}
              >
                {item.icon && (
                  <span className={cn("w-4 h-4 flex-shrink-0", item.disabled && "opacity-50")}>
                    {item.icon}
                  </span>
                )}
                {item.label}
              </button>
            </Fragment>
          ))}
        </div>
      )}
    </div>
  );
};
